import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from 'src/app/data.service';

/**
 *  CSALÁDSZERKESZTŐ – az osztályba sorolás mintájára.
 *
 *  Bal oldalon a családok, jobb oldalon a kiválasztott család tagjai és a
 *  kereső, amiből tanuló vagy gondviselő tehető hozzá. Azért kell kézi felület,
 *  mert a KIR-importból hiányozhat egy testvér vagy egy szülő: a gondviselő
 *  felhasználóneve a gyerek azonosítójából képződik, így ugyanaz a személy a
 *  másik gyereknél más néven jön létre.
 *
 *  Egy tag mindig CSAK EGY családban lehet – a pénzügyi egyenleg különben
 *  kétszer számolna –, ezért a hozzáadás a régi családból kiveszi.
 */
@Component({
  selector: 'app-csalad',
  templateUrl: './csalad.component.html',
  styleUrls: ['./csalad.component.scss'],
})
export class CsaladComponent implements OnInit {
  intezmenyId = '';
  toltodik = false;

  csaladok: any[] = [];
  nevtar: any[] = [];
  csaladNelkul: any[] = [];
  osszesites: any = { csalad: 0, tanulo_csalad_nelkul: 0, szulo_csalad_nelkul: 0 };

  kivalasztott: any = null;
  tagok: any[] = [];                 // a szerkesztés alatti tagok (megjelenítendő adattal)
  kapcsolatokPotlasa = false;   // biztonságos alap: csak a felvett tag kapcsolódik

  csaladSzuro = '';
  keresoSzoveg = '';
  egyesitendo: string[] = [];
  csakHianyos = false;

  /* ── duplikált gondviselők ── */
  duplikatumok: any[] = [];
  duplaNyitva = false;
  osszevonas: any = null;       // { nev, megmarad, beolvad: Set }
  terv: any = null;             // a dry run eredménye
  osszevonasFut = false;

  /* ── tömeges összevonás ── */
  tomegesTerv: any = null;
  tomegesKihagy: string[] = [];      // a megmaradó felhasználónevek, amiket kihagyunk
  bizonytalanIs = false;

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    this.intezmenyId = (await this.data.getFromStorage('sajatIntezmeny'))?.intezmenyId;
    await this.betolt();
    await this.duplikatumokBetolt();
  }

  async betolt(megtartId: string = null) {
    this.toltodik = true;
    try {
      const r: any = await firstValueFrom(this.http.get(`/api/csalad/lista/${this.intezmenyId}`));
      this.csaladok = r?.csaladok || [];
      this.nevtar = r?.nevtar || [];
      this.csaladNelkul = r?.csalad_nelkul || [];
      this.osszesites = r?.osszesites || {};
      const cel = megtartId || this.kivalasztott?.csalad;
      const ujra = cel ? this.csaladok.find((cs) => cs.csalad === cel) : null;
      if (ujra) this.valaszt(ujra);
      else { this.kivalasztott = null; this.tagok = []; }
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A családok betöltése nem sikerült.', 'error');
    } finally {
      this.toltodik = false;
    }
  }

  /* ── DIAGNÓZIS ── */

  /** A hiányos családok, névvel – ez a javítandók listája. */
  get hianyosCsaladok(): any[] {
    return this.csaladok.filter((cs) => (cs.hianyzo_kapcsolat || []).length);
  }

  /**
   *  A képernyő tetején álló diagnózis: MI a baj, HÁNY érintett, és MIVEL javítható.
   *  Azért itt áll össze, hogy a felület csak megjelenítsen – a döntés (mi számít
   *  hibának) egy helyen legyen leírva.
   */
  get diagnozis(): any[] {
    const ki = [];

    if (this.hianyosCsaladok.length) {
      ki.push({
        szint: 'hiba',
        cim: `${this.hianyosCsaladok.length} családban hiányzik gondviselő-kapcsolat`
          + ` (${this.osszesites.hianyzo_kapcsolat || 0} db)`,
        magyarazat: 'Ezekben a családokban a szülő belépéskor nem látja mindegyik gyerekét, '
          + 'pedig egy családban vannak. Egy gombbal pótolható.',
        tetelek: this.hianyosCsaladok.map((cs) => ({
          cimke: cs.cimke, db: cs.hianyzo_kapcsolat.length, csalad: cs.csalad,
        })),
        muvelet: 'kapcsolat',
      });
    }

    if (this.duplikatumok.length) {
      const bizonytalan = this.duplikatumok.filter((d) => d.bizonytalan).length;
      ki.push({
        szint: 'figyelem',
        cim: `${this.duplikatumok.length} duplikált gondviselő`,
        magyarazat: 'Ugyanaz a személy több felhasználónévvel szerepel, ezért az adatai '
          + 'két fiók alatt futnak. Ez okozza a hiányzó kapcsolatokat is.'
          + (bizonytalan ? ` ${bizonytalan} jelöltet ellenőrizni kell (eltérő e-mail).` : ''),
        tetelek: this.duplikatumok.map((d) => ({
          cimke: d.nev + (d.bizonytalan ? ' ⚠' : ''),
          db: d.fiokok.length,
        })),
        gomb: 'Összevonó megnyitása', muvelet: 'duplikatum',
      });
    }

    const arva = (this.osszesites.tanulo_csalad_nelkul || 0) + (this.osszesites.szulo_csalad_nelkul || 0);
    if (arva) {
      ki.push({
        szint: 'figyelem',
        cim: `${this.osszesites.tanulo_csalad_nelkul || 0} tanuló és `
          + `${this.osszesites.szulo_csalad_nelkul || 0} gondviselő nincs egyetlen családban sem`,
        magyarazat: 'Nekik nincs számlázási egységük. A bal oldali „Család nélkül" listából '
          + 'tehetők be a megfelelő családba.',
        tetelek: this.csaladNelkul.map((t) => ({ cimke: `${t.nev || t.azonosito}`, db: null })),
        gomb: null, muvelet: null,
      });
    }

    return ki;
  }

  async diagnozisMuvelet(muvelet: string) {
    if (muvelet === 'kapcsolat') await this.kapcsolatPotlas(true);
    if (muvelet === 'duplikatum') {
      this.duplaNyitva = true;
      await this.tomegesProba();
    }
  }

  /* ── duplikált gondviselők összevonása ── */

  async duplikatumokBetolt() {
    try {
      const r: any = await firstValueFrom(
        this.http.get(`/api/csalad/szulo-duplikatumok/${this.intezmenyId}`));
      this.duplikatumok = r?.duplikatumok || [];
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A duplikátumok lekérése nem sikerült.', 'error');
    }
  }

  /** Egy jelölt megnyitása összevonásra: alapból a javasolt fiók marad, a többi beolvad. */
  osszevonasIndit(jelolt: any) {
    this.osszevonas = {
      nev: jelolt.nev,
      bizonytalan: jelolt.bizonytalan,
      bizonytalan_ok: jelolt.bizonytalan_ok,
      fiokok: jelolt.fiokok,
      megmarad: jelolt.javasolt,
      beolvad: jelolt.fiokok.filter((f: any) => f.username !== jelolt.javasolt).map((f: any) => f.username),
    };
    this.terv = null;
  }

  megmaradValt(username: string) {
    if (!this.osszevonas) return;
    this.osszevonas.megmarad = username;
    // A megmaradón kívül minden más beolvad – a korábbi kijelölés különben ellentmondana.
    this.osszevonas.beolvad = this.osszevonas.fiokok
      .filter((f: any) => f.username !== username).map((f: any) => f.username);
    this.terv = null;
  }

  beolvadValt(username: string, be: boolean) {
    if (!this.osszevonas) return;
    this.osszevonas.beolvad = be
      ? [...new Set([...this.osszevonas.beolvad, username])]
      : this.osszevonas.beolvad.filter((x: string) => x !== username);
    this.terv = null;
  }

  /* ── tömeges összevonás: a rendszer szedi listába, nem kell egyesével ── */

  /** Tömeges próba. Csak számol – a listából még bármit ki lehet venni. */
  async tomegesProba() {
    this.osszevonasFut = true;
    this.osszevonas = null;
    try {
      this.tomegesTerv = await firstValueFrom(this.http.post('/api/csalad/szulo-osszevonas-tomeges', {
        intezmeny: this.intezmenyId,
        dry_run: true,
        bizonytalan_is: this.bizonytalanIs,
        kihagy: this.tomegesKihagy,
      }));
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A tömeges próba nem futott le.', 'error');
    } finally {
      this.osszevonasFut = false;
    }
  }

  tomegesKihagyValt(megmarad: string, be: boolean) {
    // A pipa azt jelenti: menjen. Kipipálatlan = kihagyandó.
    this.tomegesKihagy = be
      ? this.tomegesKihagy.filter((x) => x !== megmarad)
      : [...new Set([...this.tomegesKihagy, megmarad])];
    this.tomegesTerv = null;
  }

  tomegesBenne(megmarad: string): boolean {
    return !this.tomegesKihagy.includes(megmarad);
  }

  get tomegesFuttathato(): any[] {
    return (this.tomegesTerv?.tetelek || []).filter((t: any) => !t.kihagyva && !t.hiba);
  }

  /** Éles tömeges összevonás – csak a próba után, megerősítéssel. */
  async tomegesOsszevon() {
    if (!this.tomegesTerv) return;
    const db = this.tomegesTerv?.osszesites?.osszevonhato || 0;
    if (!db) { this.data.sendToast('Nincs mit összevonni.', 'warning'); return; }
    if (!confirm(`${db} gondviselőt vonsz össze véglegesen `
      + `(${this.tomegesTerv?.osszesites?.erintett_sor || 0} sor módosul). Ez nem vonható vissza.`)) return;
    this.osszevonasFut = true;
    try {
      const r: any = await firstValueFrom(this.http.post('/api/csalad/szulo-osszevonas-tomeges', {
        intezmeny: this.intezmenyId,
        dry_run: false,
        bizonytalan_is: this.bizonytalanIs,
        kihagy: this.tomegesKihagy,
      }));
      this.data.sendToast(`${r?.osszesites?.osszevonhato || 0} gondviselő összevonva.`
        + (r?.osszesites?.kihagyva ? ` ${r.osszesites.kihagyva} kihagyva.` : ''), 'success');
      this.tomegesTerv = null;
      await this.duplikatumokBetolt();
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A tömeges összevonás nem sikerült.', 'error');
    } finally {
      this.osszevonasFut = false;
    }
  }

  /** Próba: megmutatja, mi változna – nem ír semmit. */
  async proba() {
    if (!this.osszevonas?.megmarad || !this.osszevonas?.beolvad?.length) return;
    this.osszevonasFut = true;
    try {
      this.terv = await firstValueFrom(this.http.post('/api/csalad/szulo-osszevonas', {
        megmarad: this.osszevonas.megmarad,
        beolvad: this.osszevonas.beolvad,
        dry_run: true,
      }));
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A próba nem futott le.', 'error');
    } finally {
      this.osszevonasFut = false;
    }
  }

  get tervOsszesen(): number {
    return (this.terv?.terv || []).reduce((n: number, x: any) => n + Number(x.db || 0), 0);
  }

  /** Éles összevonás – csak a próba után, és csak megerősítéssel. */
  async osszevon() {
    if (!this.terv || !this.osszevonas) return;
    if (!confirm(`Véglegesen összevonod? ${this.osszevonas.beolvad.join(', ')} adatai `
      + `átkerülnek ide: ${this.osszevonas.megmarad}, majd a beolvadó gondviselő-rekordok törlődnek. `
      + 'Ez nem vonható vissza.')) return;
    this.osszevonasFut = true;
    try {
      const r: any = await firstValueFrom(this.http.post('/api/csalad/szulo-osszevonas', {
        megmarad: this.osszevonas.megmarad,
        beolvad: this.osszevonas.beolvad,
        dry_run: false,
      }));
      this.data.sendToast(`Összevonva: ${r?.torolt_szulo || 0} gondviselő-rekord megszűnt.`, 'success');
      this.osszevonas = null;
      this.terv = null;
      await this.duplikatumokBetolt();
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az összevonás nem sikerült.', 'error');
    } finally {
      this.osszevonasFut = false;
    }
  }

  /* ── kiválasztás ── */

  valaszt(cs: any) {
    this.kivalasztott = cs;
    this.tagok = [...(cs?.tagok || [])];
    this.egyesitendo = [];
    this.keresoSzoveg = '';
  }

  ujCsalad() {
    this.kivalasztott = { csalad: null, cimke: '(új család)', tagok: [] };
    this.tagok = [];
    this.egyesitendo = [];
    this.keresoSzoveg = '';
  }

  get szurtCsaladok(): any[] {
    const sz = this.csaladSzuro.trim().toLowerCase();
    let lista = this.csaladok;
    if (this.csakHianyos) lista = lista.filter((cs) => (cs.hianyzo_kapcsolat || []).length);
    if (!sz) return lista;
    return lista.filter((cs) =>
      (cs.cimke || '').toLowerCase().includes(sz)
      || (cs.tagok || []).some((t: any) =>
        (t.nev || '').toLowerCase().includes(sz) || (t.azonosito || '').toLowerCase().includes(sz)));
  }

  /**
   *  A kiválasztott család hiányzó gondviselő-kapcsolatai.
   *
   *  Ez az, ami a szülő BELÉPÉSKORI gyereklistáját meghatározza: a /user lekérdezés
   *  a `tanulo_szulo` táblából dolgozik, nem a családtagságból. Ezért lehet a család
   *  hiánytalan úgy, hogy a szülő mégis csak az egyik gyerekét látja.
   */
  get hianyzoKapcsolatok(): any[] {
    return this.kivalasztott?.hianyzo_kapcsolat || [];
  }

  /**
   *  Ugyanaz a személy több felhasználónévvel. A gondviselő felhasználóneve a gyerek
   *  azonosítójából képződik (…G01/G02), ezért két testvérnél két külön fiók jön
   *  létre ugyanannak a szülőnek. Csak jelezzük – a fiókok összevonása belépést
   *  érintő művelet, azt nem intézzük el magunktól.
   */
  get duplaSzulok(): any[] {
    const nevek = new Map<string, any[]>();
    for (const t of this.szuloTagok) {
      const kulcs = (t.nev || '').trim().toLowerCase();
      if (!kulcs) continue;
      nevek.set(kulcs, [...(nevek.get(kulcs) || []), t]);
    }
    return [...nevek.values()].filter((lista) => lista.length > 1);
  }

  /* ── tagok szerkesztése ── */

  get tanuloTagok() { return this.tagok.filter((t) => t.tipus === 'tanulo'); }
  get szuloTagok() { return this.tagok.filter((t) => t.tipus !== 'tanulo'); }

  /**
   *  A kereső találatai. A már bepakolt tagok kimaradnak; a többinél kiírjuk, ha
   *  másik családban vannak – onnan a mentés kiveszi őket, és ezt látni kell előre.
   */
  get talalatok(): any[] {
    const sz = this.keresoSzoveg.trim().toLowerCase();
    if (sz.length < 2) return [];
    const bent = new Set(this.tagok.map((t) => t.azonosito));
    return this.nevtar
      .filter((t) => !bent.has(t.azonosito))
      .filter((t) => (t.nev || '').toLowerCase().includes(sz)
        || (t.azonosito || '').toLowerCase().includes(sz)
        || (t.osztaly || '').toLowerCase().includes(sz))
      .slice(0, 25)
      .map((t) => ({ ...t, jelenlegiCsalad: this.melyikCsaladban(t.azonosito) }));
  }

  melyikCsaladban(azonosito: string): any {
    return this.csaladok.find((cs) => (cs.tagok || []).some((t: any) => t.azonosito === azonosito)) || null;
  }

  hozzaad(tag: any) {
    if (this.tagok.some((t) => t.azonosito === tag.azonosito)) return;
    this.tagok = [...this.tagok, { ...tag }];
    this.keresoSzoveg = '';
  }

  kivesz(tag: any) {
    this.tagok = this.tagok.filter((t) => t.azonosito !== tag.azonosito);
  }

  /* ── mentés ── */

  async mentes() {
    if (!this.kivalasztott) return;
    const azonositok = this.tagok.map((t) => t.azonosito);

    if (!azonositok.length && !this.kivalasztott.csalad) {
      this.data.sendToast('Adj hozzá legalább egy tagot.', 'warning');
      return;
    }
    // A tagok elvétele más családból visszafordíthatatlan, ezért kimondjuk előre.
    const elvett = this.tagok
      .map((t) => ({ t, cs: this.melyikCsaladban(t.azonosito) }))
      .filter((x) => x.cs && x.cs.csalad !== this.kivalasztott.csalad);
    if (elvett.length && !confirm(
      `${elvett.length} tag jelenleg MÁSIK családban van (${elvett.map((x) => x.t.nev || x.t.azonosito).join(', ')}).`
      + ' A mentés áthelyezi őket ide. Folytatod?')) return;

    const body = { tagok: azonositok, kapcsolatok_potlasa: this.kapcsolatokPotlasa };
    try {
      const r: any = this.kivalasztott.csalad
        ? await firstValueFrom(this.http.put(`/api/csalad/${this.kivalasztott.csalad}`, body))
        : await firstValueFrom(this.http.post(`/api/csalad/${this.intezmenyId}`, body));
      this.data.sendToast(
        (r?.torolve ? 'A család törölve (nem maradt tagja).' : 'Család mentve.')
        + (r?.uj_kapcsolat ? ` ${r.uj_kapcsolat} új gondviselő-kapcsolat jött létre.` : ''),
        'success');
      await this.betolt(r?.csalad || null);
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A mentés nem sikerült.', 'error');
    }
  }

  /* ── egyesítés ── */

  egyesitesValt(csaladId: string, be: boolean) {
    this.egyesitendo = be
      ? [...new Set([...this.egyesitendo, csaladId])]
      : this.egyesitendo.filter((x) => x !== csaladId);
  }

  async egyesites() {
    if (!this.kivalasztott?.csalad || !this.egyesitendo.length) return;
    const nevek = this.egyesitendo
      .map((id) => this.csaladok.find((cs) => cs.csalad === id)?.cimke || id).join(' • ');
    if (!confirm(`A kijelölt család(ok) tagjai ide kerülnek, a forrás családok megszűnnek:\n${nevek}`)) return;
    try {
      const r: any = await firstValueFrom(this.http.post(
        `/api/csalad/egyesites/${this.kivalasztott.csalad}`,
        { forrasok: this.egyesitendo, kapcsolatok_potlasa: this.kapcsolatokPotlasa }));
      this.data.sendToast(`${r?.egyesitett || 0} család egyesítve.`
        + (r?.uj_kapcsolat ? ` ${r.uj_kapcsolat} új gondviselő-kapcsolat.` : ''), 'success');
      this.egyesitendo = [];
      await this.betolt(this.kivalasztott.csalad);
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az egyesítés nem sikerült.', 'error');
    }
  }

  /** A hiányzó gondviselő-kapcsolatok pótlása – mentés nélkül, önálló műveletként. */
  async kapcsolatPotlas(mind = false) {
    const cel = mind ? 'mind' : this.kivalasztott?.csalad;
    if (!cel) return;
    if (mind && !confirm(`Az intézmény ${this.osszesites.hianyos_csalad || 0} családjában `
      + `${this.osszesites.hianyzo_kapcsolat || 0} hiányzó gondviselő-kapcsolat jön létre. Folytatod?`)) return;
    try {
      const r: any = await firstValueFrom(this.http.post(
        `/api/csalad/kapcsolat-potlas/${cel}`, { intezmeny: this.intezmenyId }));
      this.data.sendToast(`${r?.uj_kapcsolat || 0} gondviselő-kapcsolat létrejött`
        + (mind ? ` (${r?.csalad_db || 0} családban).` : '.'), 'success');
      await this.betolt(mind ? null : cel);
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A pótlás nem sikerült.', 'error');
    }
  }

  async torles() {
    if (!this.kivalasztott?.csalad) return;
    if (!confirm('Biztosan törlöd ezt a családot? A tanulók és a gondviselők megmaradnak, '
      + 'csak a családi összerendelés szűnik meg.')) return;
    try {
      await firstValueFrom(this.http.delete(`/api/csalad/${this.kivalasztott.csalad}`));
      this.data.sendToast('Család törölve.', 'success');
      this.kivalasztott = null;
      this.tagok = [];
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A törlés nem sikerült.', 'error');
    }
  }
}
